import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  ViewChild
} from '@angular/core';

import { LOGOUT_STATE_PLACEHOLDER, LOGOUT_TOKEN_PLACEHOLDER, LogoutConfig } from '@hdms/shared/data-access';
import { ButtonBaseClassesType, ButtonModule } from '@hdms/shared/ui-core';
import { AnalyticsService, EnvironmentService, LogoutModule, TokenService } from '@hdms/shared/util-core';

interface LogoutDetailsResponse {
  cloudSsoLogoutUri: string;
  parameters: Record<string, string>;
}

const LOGOUT_FORM_SELECTOR = '.js-logout-form';

@Component({
  standalone: true,
  selector: 'shrd-pf-ft-header-user-logout',
  templateUrl: './header-user-logout.component.html',
  imports: [CommonModule, ButtonModule, LogoutModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderUserLogoutComponent {
  @Input() logoutConfig: LogoutConfig;
  @Input() buttonVariant: ButtonBaseClassesType;
  @Input() buttonClasses: string;
  @Input() logoutLabel: string;

  logoutDetailsResponse: LogoutDetailsResponse;
  logoutParamKeys: string[] = [];
  logoutInProgress = false;

  @ViewChild('logoutForm', { read: ElementRef })
  private readonly logoutForm: ElementRef<HTMLFormElement>;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly cdr: ChangeDetectorRef,
    private readonly env: EnvironmentService,
    private readonly tokenService: TokenService,
    private readonly analyticsService: AnalyticsService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  doLogout(): void {
    this.logoutInProgress = true;
    this.analyticsService.sendEvent({
      action: 'Account',
      category: 'UserAccountFlyout',
      label: 'Logout'
    });
    this.env.hostContext ? this.triggerLegacyLogout() : this.fetchLogoutDetails();
  }

  private triggerLegacyLogout(): void {
    this.document.querySelector<HTMLButtonElement>(`${LOGOUT_FORM_SELECTOR} button`)?.click();
  }

  private fetchLogoutDetails(): void {
    this.httpClient.get<LogoutDetailsResponse>(this.logoutConfig.url).subscribe({
      next: data => {
        const { parameters } = (this.logoutDetailsResponse = data);
        this.logoutParamKeys = Object.keys(parameters);

        this.logoutParamKeys.forEach(key => {
          if (parameters[key] === LOGOUT_TOKEN_PLACEHOLDER) {
            parameters[key] = this.tokenService.bearerToken;
          }
          if (parameters[key] === LOGOUT_STATE_PLACEHOLDER) {
            parameters[key] = this.logoutConfig.state;
          }
        });

        this.cdr.detectChanges();
        this.logoutForm.nativeElement.submit();
      },
      error: _ => {
        this.logoutInProgress = false;
        this.cdr.markForCheck();
      }
    });
  }
}
